import {z} from 'zod';

// Environment schema that can be used for validation
export const envSchema = z.object({
  AUTHENTICATION_PROVIDER: z.enum(['entra', 'auth0']).default('entra'),
  AUTH0_ISSUER: z.string().default(''),
  AUTH0_UI_URL: z.string().default(''),
  AUTH0_STAFF_CONNECTION: z.string().regex(/^[A-Za-z0-9_-]{0,128}$/).default(''),
  LOCAL_CLIENT_PREPARATION_ENABLED: z.enum(['true', 'false']).default('true').transform(value => value === 'true'),
  INTERNAL_ADMIN_EMAIL_DOMAINS: z.string().default('').transform(value => value.split(',').map(x => x.trim().toLowerCase()).filter(Boolean)).refine(domains => domains.every(domain => /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/.test(domain)), 'Invalid staff domain configuration'),
  // Service Configuration
  NODE_ENV: z.enum(['development', 'beta', 'staging', 'test', 'production']).default('development'),
  PORT: z.string().default('4205').transform(Number),

  // API Endpoints
  SHINE_AI_BASE_URL: z.string().url(),
  SHINE_ENRICH_BASE_URL: z.string().url(),
  SHINE_AI_WEB_URL: z.string().url(),
    SHINE_CONNECT_API_URL: z.string().url(),
  SHINE_TRANSLATIONS_URL: z.string().url(),
  SHINE_WEBINARS_URL: z.string().url().default('http://localhost:3001'),
  SHINE_AUTH_EXTERNAL_URL: z.string().url(),

  // Frontend Configuration
  HOSTING_URL: z.string().url(),
  WIDGET_SCRIPT_URL: z.string().url(),
  FRONTEND_DUMMY_URL: z.string().url(),
  AZURE_AD_CLIENT_ID: z.string(),
  AZURE_AD_AUTHORITY: z.string(),

  // Logging Configuration
  LOG_LEVEL: z.enum(['error', 'warn', 'info', 'http', 'verbose', 'debug', 'silly']).default('info'),
  METRICS_ENABLED: z.string().default('true').transform(a => a === 'true'),
  TRACING_ENABLED: z.string().default('true').transform(a => a === 'true'),
}).superRefine((config, ctx) => {
  if (config.AUTHENTICATION_PROVIDER !== 'auth0') return;
  const fail = (key: string, message: string) => ctx.addIssue({ code: 'custom', path: [key], message });
  try {
    const issuer = new URL(config.AUTH0_ISSUER);
    if (issuer.protocol !== 'https:' || !issuer.hostname.endsWith('.auth0.com') || issuer.pathname !== '/' ||
        issuer.username || issuer.password || issuer.port || issuer.search || issuer.hash) {
      fail('AUTH0_ISSUER', 'A canonical Auth0 HTTPS issuer is required');
    }
  } catch { fail('AUTH0_ISSUER', 'A canonical Auth0 HTTPS issuer is required'); }
  if (!['development', 'beta', 'staging', 'production'].includes(config.NODE_ENV)) {
    fail('NODE_ENV', 'Auth0 requires a configured local or hosted environment');
    return;
  }
  const hostedAuth0 = ['staging', 'production'].includes(config.NODE_ENV);
  if (hostedAuth0) {
    try {
      const auth0Ui = new URL(config.AUTH0_UI_URL);
      const hosting = new URL(config.HOSTING_URL);
      if (auth0Ui.protocol !== 'https:' || auth0Ui.pathname !== '/' || auth0Ui.port || auth0Ui.username ||
          auth0Ui.password || auth0Ui.search || auth0Ui.hash || !auth0Ui.hostname.includes('.') ||
          ['localhost', '127.0.0.1', '[::1]'].includes(auth0Ui.hostname) ||
          hosting.origin !== auth0Ui.origin || hosting.pathname !== '/') {
        fail('AUTH0_UI_URL', 'Hosted Auth0 requires an exact dedicated HTTPS Auth0 UI origin matching HOSTING_URL');
      }
    } catch { fail('AUTH0_UI_URL', 'Hosted Auth0 requires an exact dedicated HTTPS Auth0 UI origin matching HOSTING_URL'); }
  }
  for (const key of ['HOSTING_URL', 'SHINE_AUTH_EXTERNAL_URL', 'SHINE_AI_BASE_URL', 'SHINE_AI_WEB_URL',
    'SHINE_ENRICH_BASE_URL', 'SHINE_CONNECT_API_URL', 'SHINE_WEBINARS_URL', 'SHINE_TRANSLATIONS_URL',
    'WIDGET_SCRIPT_URL', 'FRONTEND_DUMMY_URL'] as const) {
    try {
      const url = new URL(config[key]);
      const allowed = config.NODE_ENV !== 'development' ? url.protocol === 'https:' &&
        !['localhost', '127.0.0.1', '[::1]'].includes(url.hostname) :
        url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname);
      const protectedResource = !['WIDGET_SCRIPT_URL', 'FRONTEND_DUMMY_URL'].includes(key);
      if (!allowed || url.username || url.password || (protectedResource && url.search) || url.hash) {
        fail(key, 'Auth0 resource URLs must use the configured environment transport without credentials or query data');
      }
    } catch { fail(key, 'A valid Auth0 resource URL is required'); }
  }
});

// TypeScript interface derived from the schema
// Property names match how they're used in the application
export type Environment = {
  production: boolean;
  environment: string;
  hostingUrl: string;
  aiBaseServerUrl: string;
    connectBaseServerUrl: string;
  translationsBaseServerUrl: string;
  enrichBaseServerUrl: string;
  aiWebServerUrl: string;
    webinarsServerUrl: string;
  authBaseServerUrl: string;
  widgetScriptUrl: string;
  frontendDummy: string;
  logLevel: string;
  metricsEnabled: boolean;
  tracingEnabled: boolean;
  msalEnabled: boolean;
  localClientPreparationEnabled: boolean;
  authenticationProvider: 'entra' | 'auth0';
  auth0Issuer: string;
  auth0UiUrl: string;
  auth0StaffConnection: string;
  internalAdminEmailDomains: string[];
  azureAdClientId: string;
  azureAdAuthority: string;
}; 
